import Icon from '@/components/UI/Icon/Icon';
import { _isNotBlank } from '@/littledash';
import type { AgentSessionApiId, AgentTreeNodeApiId } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { type FC, useEffect, useRef, useState } from 'react';
import styles from './AgentFeedback.module.scss';

interface AgentFeedbackProps {
  sessionApiId: AgentSessionApiId;
  nodeApiId: AgentTreeNodeApiId;
  testId?: string;
}

const COMMENT_MAX_LENGTH = 2000;
const COMMENT_PLACEHOLDER = 'Let us know what went wrong or how we can improve...';

const submittedKey = (nodeApiId: AgentTreeNodeApiId): string => `agent-feedback-submitted:${nodeApiId}`;

/**
 * A storage-blocked context (Safari private browsing, a cookie-blocked iframe) throws on the
 * property access itself, not just the call — so this must not throw during render and take the
 * whole report modal down with it.
 */
const readSubmitted = (nodeApiId: AgentTreeNodeApiId): boolean => {
  try {
    return sessionStorage.getItem(submittedKey(nodeApiId)) === 'true';
  } catch {
    return false;
  }
};

const rememberSubmitted = (nodeApiId: AgentTreeNodeApiId): void => {
  try {
    sessionStorage.setItem(submittedKey(nodeApiId), 'true');
  } catch {
    // Same bargain as the read: the thank-you shows for this mount, it just will not survive one.
  }
};

/**
 * Thumbs up / thumbs down control for an agent report, delivered to Slack rather than persisted.
 *
 * Thumbs up submits straight away. Thumbs down requires a comment before it can be sent, because a
 * rating without a reason is not actionable — so a bare thumbs down is deliberately impossible.
 *
 * The `sessionStorage` record of submission is a courtesy, not a guarantee: nothing is persisted
 * server-side, and sessionStorage is scoped to the tab, so it survives a reload but a new tab,
 * another browser, or closing this one lets the same report be rated again.
 *
 * Enter submits; Escape abandons. Neither is discoverable from the markup.
 */
const AgentFeedback: FC<AgentFeedbackProps> = ({ sessionApiId, nodeApiId, testId = 'agent-feedback' }) => {
  const [commenting, setCommenting] = useState(false);
  const [positiveChosen, setPositiveChosen] = useState(false);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(() => readSubmitted(nodeApiId));
  const [failed, setFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const thumbDownRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusRef = useRef(false);

  // Abandoning unmounts whatever had focus, which would drop keyboard users to the document body.
  useEffect(() => {
    if (!commenting && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      thumbDownRef.current?.focus();
    }
  }, [commenting]);

  const { invoke } = useApiHook({
    endpoint: 'POST /api/v1/ai-agents/sessions/{sessionApiId}/nodes/{nodeApiId}/feedback',
    path: { sessionApiId, nodeApiId },
    options: {
      invokeOnInit: false,
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-node-feedback-submit',
      },
    },
  });

  /** Returns to the thumbs, discarding whatever was typed. Bound to the ✕ and to Escape. */
  const abandon = (): void => {
    restoreFocusRef.current = true;
    setCommenting(false);
    setComment('');
    setFailed(false);
  };

  const submit = async (body: { sentiment: 'positive' | 'negative'; comment?: string }): Promise<void> => {
    setSubmitting(true);
    setFailed(false);
    const response = await invoke({ path: { sessionApiId, nodeApiId }, body }).catch(() => null);
    setSubmitting(false);

    if (response?.type === 'success') {
      rememberSubmitted(nodeApiId);
      setSubmitted(true);
      return;
    }
    // A tinted thumb with aria-pressed="true" beside an error claims the rating was accepted.
    setPositiveChosen(false);
    setFailed(true);
  };

  if (submitted) {
    return (
      <div
        className={styles.success}
        role="status"
        data-testid={testId}
        data-test-component="AgentFeedback"
        data-test-element="success"
      >
        Thank you for your feedback
        <Icon icon="success" className={styles.successIcon} viewBox="-1 -1 22 22" width={18} height={18} />
      </div>
    );
  }

  // Escape sits on the container, not the input, so it still fires once focus has moved to the ✕ or
  // the send button.
  if (commenting) {
    return (
      <div
        className={styles.container}
        role="group"
        aria-label="Report feedback"
        data-testid={testId}
        data-test-component="AgentFeedback"
        data-test-element="comment-row"
        onKeyDown={(event) => {
          // Escape during IME composition cancels the candidate; it must not discard the draft.
          if (event.key === 'Escape' && event.nativeEvent.isComposing !== true) {
            event.stopPropagation();
            abandon();
          }
        }}
      >
        {failed ? (
          <p className={styles.error} role="alert" data-test-element="error">
            Couldn&apos;t send that feedback. Try again.
          </p>
        ) : null}
        <input
          type="text"
          className={styles.comment}
          data-test-element="comment"
          placeholder={COMMENT_PLACEHOLDER}
          aria-label={COMMENT_PLACEHOLDER}
          value={comment}
          maxLength={COMMENT_MAX_LENGTH}
          disabled={submitting}
          autoFocus
          onChange={(event) => setComment(event.target.value)}
          onKeyDown={(event) => {
            // `isComposing` keeps Enter from submitting a half-converted CJK candidate, and
            // `repeat` keeps a held key from firing twice; same guard as Form.tsx:427.
            if (
              event.key !== 'Enter' ||
              event.nativeEvent.isComposing === true ||
              event.repeat ||
              !_isNotBlank(comment) ||
              submitting
            ) {
              return;
            }
            void submit({ sentiment: 'negative', comment });
          }}
        />
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Close feedback"
          disabled={submitting}
          onClick={abandon}
          data-test-element="abandon"
        >
          <Icon icon="close" width={12} height={12} />
        </button>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.iconButtonPrimary}`}
          aria-label="Send feedback"
          disabled={!_isNotBlank(comment) || submitting}
          onClick={() => void submit({ sentiment: 'negative', comment })}
          data-test-element="send"
        >
          {/* `send` is a 24x24 Material path, so it needs its own viewBox rather than Icon's 32x32. */}
          <Icon icon="send" width={16} height={16} viewBox="0 0 24 24" />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid={testId} data-test-component="AgentFeedback" data-test-element="rate">
      {failed ? (
        <p className={styles.error} role="alert" data-test-element="error">
          Couldn&apos;t send that feedback. Try again.
        </p>
      ) : null}
      <span className={styles.prompt} data-test-element="prompt">
        Was this helpful?
      </span>
      <button
        type="button"
        className={`${styles.thumb} ${styles.thumbUp} ${positiveChosen ? styles.thumbUpSelected : ''}`}
        aria-label="This report was helpful"
        aria-pressed={positiveChosen}
        data-test-element="thumb-up"
        disabled={submitting}
        onClick={() => {
          setPositiveChosen(true);
          void submit({ sentiment: 'positive' });
        }}
      >
        <Icon icon="thumb_up" width={16} height={16} />
      </button>
      <button
        type="button"
        className={`${styles.thumb} ${styles.thumbDown}`}
        ref={thumbDownRef}
        aria-label="This report was not helpful"
        aria-pressed={false}
        data-test-element="thumb-down"
        disabled={submitting}
        onClick={() => {
          setCommenting(true);
          setFailed(false);
        }}
      >
        <Icon icon="thumb_down" width={16} height={16} />
      </button>
    </div>
  );
};

export default AgentFeedback;
